import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMarket } from '../../context/MarketContext';
import {
  cimmpleRxFaqs,
  cimmpleRxLaunchOffersConfig,
  cimmpleRxOnboardingConfig,
  cimmpleRxPlans,
  cimmpleRxPricingTimeline,
  getCimmpleRxLaunchOfferByModalPlan,
  getEnabledCimmpleRxLaunchOffers,
} from '../../data/cimmpleRxPricing';

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  notIncluded?: string[];
  highlighted: boolean;
  cta: string;
}

interface FormData {
  name: string;
  email: string;
  company: string;
}

interface FormErrors {
  name?: string;
  email?: string;
}

const cimmplePlans: PricingPlan[] = [
  {
    name: 'Free Trial',
    price: '$0',
    period: 'for 3 months',
    description: 'Experience CimmpleFlow features free. No credit card required.',
    features: [
      'All CimmpleFlow features included',
      'Quotations (VendorConnect™)',
      'Job Management (with live tracking)',
      'Quality Control (live reporting)',
      'Reporting (with scheduling)',
      'Accounting (advanced)',
      'AI-powered invoice extraction (PDF & email)',
      'Timesheets (3rd party integration)',
      'Document Storage (version control)',
      '2 users included',
      '50 orders limit',
      '1 GB storage limit',
    ],
    highlighted: false,
    cta: 'Start Free Trial',
  },
  {
    name: 'CimmpleFlow',
    price: '$69',
    period: '/user/month',
    description: 'Billed annually. $77/user if billed monthly.',
    features: [
      'Quotations (VendorConnect™)',
      'Order Management',
      'Purchasing',
      'Job Management (with live tracking)',
      'Quality Control (live reporting)',
      'Reporting (with scheduling)',
      'Accounting (advanced)',
      'AI-powered invoice extraction (PDF & email)',
      'Timesheets (3rd party integration)',
      'Document Storage (version control)',
      'Per-user pricing',
      'Unlimited Jobs/Customers',
    ],
    highlighted: true,
    cta: 'Get Started',
  },
];

const cimmplePayExtraCostFeatures = new Set([
  'Tax compliance & filing',
]);

const payrollPlans: PricingPlan[] = [
  {
    name: 'CimmplePay',
    price: '$29',
    period: '/month',
    description: 'Plus $3 per employee payroll',
    features: [
      'Automated salary calculations',
      'Employee self-service portal',
      'Leave & attendance tracking',
      'Comprehensive reporting',
      'Unlimited employees',
      'Email support',
      '$3 per employee per payroll run',
      'Tax compliance & filing',
      'Biometric face-recognition attendance',
    ],
    highlighted: true,
    cta: 'Get Started',
  },
];

const cimmpleFlowIndiaPlans: PricingPlan[] = [
  {
    name: 'CimmpleFlow',
    price: '₹4,000',
    period: '/month',
    description: 'Complete ERP for manufacturing and logistics operations.',
    features: [
      'Quotations with VendorConnect™',
      'Order and purchase management',
      'Job management with live tracking',
      'Quality control and reporting',
      'Accounting and invoice management',
      'AI-assisted invoice extraction',
      'Document storage and version control',
    ],
    highlighted: true,
    cta: 'Get Started',
  },
];

const Pricing: React.FC = () => {
  const { market } = useMarket();
  const [searchParams] = useSearchParams();
  const [activeProduct, setActiveProduct] = useState<'cimmple' | 'payroll' | 'rx'>(() => {
    const product = searchParams.get('product');
    if (market === 'IN') return product === 'cimmpleflow' ? 'cimmple' : 'rx';
    return product === 'cimmlepay' ? 'payroll' : 'cimmple';
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      modalRef.current?.querySelector('input')?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);
    setShowModal(true);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const response = await fetch('/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          plan: selectedPlan,
        }),
      });

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to send request. Please try again.';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          errorMessage = `Server error (${response.status}): ${errorText.substring(0, 100)}`;
        }
        alert(errorMessage);
        return;
      }

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
      } else {
        alert(result.message || 'Failed to send request. Please try again.');
      }
    } catch (error) {
      console.error('Email submission error:', error);
      alert('Failed to send request. Please check your connection and try again, or contact us directly at contact@cimmple.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsSubmitted(false);
    setFormData({ name: '', email: '', company: '' });
    setErrors({});
  };

  const currentPlans =
    activeProduct === 'rx'
      ? cimmpleRxPlans
      : activeProduct === 'cimmple'
        ? market === 'IN' ? cimmpleFlowIndiaPlans : cimmplePlans
        : payrollPlans;
  const usesPrimaryTheme = activeProduct !== 'payroll';
  const showCimmpleRxExtras = market === 'IN' && activeProduct === 'rx';
  const enabledLaunchOffers = getEnabledCimmpleRxLaunchOffers();
  const showLaunchOffers = showCimmpleRxExtras && enabledLaunchOffers.length > 0;
  const showPricingTimeline = showCimmpleRxExtras && cimmpleRxPricingTimeline.enabled;
  const showOnboarding = showCimmpleRxExtras && cimmpleRxOnboardingConfig.enabled;
  const selectedLaunchOffer = getCimmpleRxLaunchOfferByModalPlan(selectedPlan);

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
            Pricing
          </span>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3">
            Simple, transparent <span className="gradient-text">pricing</span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-dark-300 max-w-2xl mx-auto mb-10">
            Choose the plan that fits your business. All plans include our core features 
            with no hidden fees or surprises.
          </p>

          {market === 'US' ? (
            <div className="inline-flex items-center p-1.5 rounded-2xl bg-slate-100 dark:bg-dark-800/50 border border-slate-200 dark:border-dark-700">
            <button
              onClick={() => setActiveProduct('cimmple')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeProduct === 'cimmple'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25'
                  : 'text-slate-600 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                CimmpleFlow
              </span>
            </button>
            <button
              onClick={() => setActiveProduct('payroll')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeProduct === 'payroll'
                  ? 'bg-gradient-to-r from-accent-500 to-accent-600 text-white shadow-lg shadow-accent-500/25'
                  : 'text-slate-600 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                CimmplePay
              </span>
            </button>
            </div>
          ) : (
            <div className="inline-flex items-center p-1.5 rounded-2xl bg-slate-100 dark:bg-dark-800/50 border border-slate-200 dark:border-dark-700">
              <button
                onClick={() => setActiveProduct('rx')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeProduct === 'rx'
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25'
                    : 'text-slate-600 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                CimmpleRx
              </button>
              <button
                onClick={() => setActiveProduct('cimmple')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeProduct === 'cimmple'
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25'
                    : 'text-slate-600 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                CimmpleFlow
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-8 ${
            currentPlans.length >= 4
              ? 'md:grid-cols-2 xl:grid-cols-4 max-w-7xl mx-auto'
              : currentPlans.length > 1
                ? 'md:grid-cols-2 max-w-4xl mx-auto'
                : 'max-w-lg mx-auto'
          }`}>
            {currentPlans.map((plan) => {
              const ctaClassName = `block text-center w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                plan.highlighted
                  ? usesPrimaryTheme
                    ? 'btn-primary'
                    : 'px-6 py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white rounded-xl hover:from-accent-400 hover:to-accent-500 transform hover:scale-[1.02] hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300'
                  : 'btn-secondary'
              }`;

              return (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-8 transition-all duration-300 ${
                  plan.highlighted
                    ? usesPrimaryTheme
                      ? 'bg-gradient-to-b from-primary-100 dark:from-primary-500/10 to-white dark:to-dark-900/50 border-2 border-primary-500/50 scale-105 shadow-xl shadow-primary-500/10'
                      : 'bg-gradient-to-b from-accent-100 dark:from-accent-500/10 to-white dark:to-dark-900/50 border-2 border-accent-500/50 scale-105 shadow-xl shadow-accent-500/10'
                    : 'card hover:border-slate-300 dark:hover:border-dark-600'
                }`}
              >
                <div className="text-center mb-8">
                  <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex flex-col items-center gap-2 mb-3">
                    {plan.name === 'CimmplePay' ? (
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-display font-bold text-slate-900 dark:text-white">
                          $29
                        </span>
                        <span className="text-slate-500 dark:text-dark-400">{plan.period}</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-display font-bold text-slate-900 dark:text-white">
                          {plan.price}
                        </span>
                        {plan.period && (
                          <span className="text-slate-500 dark:text-dark-400">{plan.period}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-slate-500 dark:text-dark-400 text-sm">
                    {plan.description}
                  </p>
                </div>

                <ul className={`space-y-4 ${plan.name === 'CimmplePay' ? 'mb-3' : 'mb-8'}`}>
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-dark-300">
                      <svg
                        className={`w-5 h-5 flex-shrink-0 ${
                          plan.highlighted 
                            ? usesPrimaryTheme ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'
                            : 'text-green-600 dark:text-green-500'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>
                        {feature}
                        {plan.name === 'CimmplePay' && cimmplePayExtraCostFeatures.has(feature) && (
                          <sup className="text-accent-600 dark:text-accent-400 font-medium">*</sup>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {plan.name === 'CimmplePay' && (
                  <p className="text-xs text-slate-500 dark:text-dark-400 mb-8 leading-relaxed">
                    <sup className="text-accent-600 dark:text-accent-400 font-medium">*</sup>{' '}
                    Available at additional cost.
                  </p>
                )}

                {plan.cta === 'Contact Sales' ? (
                  <Link to="/contact" className={ctaClassName}>
                    {plan.cta}
                  </Link>
                ) : (
                  <button
                    onClick={() => handlePlanSelect(plan.name)}
                    className={ctaClassName}
                  >
                    {plan.cta}
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {showLaunchOffers && (
        <section className="py-16 bg-slate-50 dark:bg-dark-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
                {cimmpleRxLaunchOffersConfig.eyebrow}
              </span>
              <h2 className="section-title">
                Limited-time <span className="gradient-text">launch offers</span>
              </h2>
              <p className="section-subtitle mx-auto mt-4">
                {cimmpleRxLaunchOffersConfig.subtitle}
              </p>
            </div>

            <div className={`grid gap-8 mx-auto ${
              enabledLaunchOffers.length > 1
                ? 'md:grid-cols-2 max-w-4xl'
                : 'max-w-lg'
            }`}>
              {enabledLaunchOffers.map((offer) => (
                <div
                  key={offer.id}
                  className={`relative rounded-2xl p-8 transition-all duration-300 ${
                    offer.highlighted
                      ? 'bg-gradient-to-b from-primary-100 dark:from-primary-500/10 to-white dark:to-dark-900/50 border-2 border-primary-500/50 shadow-xl shadow-primary-500/10'
                      : 'card hover:border-slate-300 dark:hover:border-dark-600'
                  }`}
                >
                  <div className="text-center mb-8">
                    <span className="inline-block px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-xs font-medium mb-3">
                      {offer.badge}
                    </span>
                    <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                      {offer.title}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1 mb-3">
                      <span className="text-4xl font-display font-bold text-slate-900 dark:text-white">
                        {offer.price}
                      </span>
                      <span className="text-slate-500 dark:text-dark-400">{offer.period}</span>
                    </div>
                    <p className="text-slate-500 dark:text-dark-400 text-sm">
                      {offer.description}
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {offer.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-slate-700 dark:text-dark-300">
                        <svg
                          className={`w-5 h-5 flex-shrink-0 ${
                            offer.highlighted
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-green-600 dark:text-green-500'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanSelect(offer.modalPlan)}
                    className={`block text-center w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                      offer.highlighted ? 'btn-primary' : 'btn-secondary'
                    }`}
                  >
                    {offer.cta}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {showPricingTimeline && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
                {cimmpleRxPricingTimeline.eyebrow}
              </span>
              <h2 className="section-title">
                How launch <span className="gradient-text">pricing works</span>
              </h2>
              <p className="section-subtitle mx-auto mt-4">
                {cimmpleRxPricingTimeline.subtitle}
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3 max-w-5xl mx-auto">
              {cimmpleRxPricingTimeline.steps.map((item) => (
                <div key={item.step} className="card p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center mx-auto mb-4 font-display font-bold text-lg">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 dark:text-dark-400 text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {showOnboarding && (
        <section className="py-16 bg-slate-50 dark:bg-dark-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
                {cimmpleRxOnboardingConfig.eyebrow}
              </span>
              <h2 className="section-title">
                Get started with <span className="gradient-text">confidence</span>
              </h2>
              <p className="section-subtitle mx-auto mt-4">
                {cimmpleRxOnboardingConfig.subtitle}
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto mb-8">
              <div className="card p-8">
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">
                  {cimmpleRxOnboardingConfig.includedAtNoExtraCost.title}
                </h3>
                <ul className="space-y-4">
                  {cimmpleRxOnboardingConfig.includedAtNoExtraCost.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-slate-700 dark:text-dark-300">
                      <svg className="w-5 h-5 flex-shrink-0 text-green-600 dark:text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-8">
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">
                  {cimmpleRxOnboardingConfig.dataMigration.title}
                </h3>
                <ul className="space-y-4">
                  {cimmpleRxOnboardingConfig.dataMigration.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-700 dark:text-dark-300">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-primary-600 dark:text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="card p-8 md:p-10 bg-gradient-to-br from-slate-50 to-primary-50/30 dark:from-dark-800/50 dark:to-primary-500/5 border-primary-500/20">
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-3">
                  {cimmpleRxOnboardingConfig.enterpriseServices.title}
                </h3>
                <p className="text-slate-600 dark:text-dark-300 text-sm mb-6">
                  {cimmpleRxOnboardingConfig.enterpriseServices.description}
                </p>
                <ul className="grid sm:grid-cols-2 gap-4 mb-8">
                  {cimmpleRxOnboardingConfig.enterpriseServices.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-slate-700 dark:text-dark-300">
                      <svg className="w-5 h-5 flex-shrink-0 text-primary-600 dark:text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="btn-primary inline-flex">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {market === 'US' && (
        <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="section-title">
              Add-ons & <span className="gradient-text">Services</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Extend your CimmpleFlow or CimmplePay subscription with optional services.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <Link
              to="/services/back-office"
              className="card group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 p-6 hover:border-primary-500/40 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                    Back-Office Data Entry
                  </h3>
                  <p className="text-slate-600 dark:text-dark-400 text-sm">
                    We receive invoices and order info via email (PDFs), enter all data into your CimmpleFlow and/or CimmplePay subscription—you focus on operations.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:items-end gap-2">
                <span className="text-slate-500 dark:text-dark-400 text-sm font-medium">Contact for pricing</span>
                <span className="inline-flex items-center gap-2 font-semibold text-primary-600 dark:text-primary-400 group-hover:gap-4 transition-all">
                  Learn more
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </Link>
          </div>
        </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {(market === 'IN'
              ? activeProduct === 'cimmple'
                ? [
                    { q: 'What is included with CimmpleFlow?', a: 'The plan includes quotations, orders, purchases, jobs, quality control, reporting, accounting, invoice capture, and document storage.' },
                    { q: 'Is the price per user?', a: 'The India plan is listed at ₹4,000 per month. Contact our team for setup and account details.' },
                    { q: 'How do I get started?', a: 'Choose the plan and send your details. Our team will contact you with the next steps.' },
                  ]
                : cimmpleRxFaqs
              : [
                  { q: 'Can I switch plans later?', a: 'Yes! You can upgrade or downgrade your plan at any time. Changes will be prorated and reflected in your next billing cycle.' },
                  { q: 'Is there a free trial?', a: 'Yes, we offer a 3-month free trial with access to all features. No credit card required to start.' },
                  { q: 'What payment methods do you accept?', a: 'We accept all major credit cards (Visa, MasterCard, American Express) and can arrange invoicing for CimmpleFlow customers.' },
                  { q: 'Can I cancel anytime?', a: 'Absolutely. You can cancel your subscription at any time. Your access will continue until the end of your current billing period.' },
                  { q: 'Do you offer discounts for annual billing?', a: 'Yes! Save up to 10% when you choose annual billing. Contact our sales team for more details.' },
                ]
            ).map((faq, i) => (
              <div key={i} className="card">
                <h3 className="text-slate-900 dark:text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-slate-600 dark:text-dark-400">{faq.a}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-600 dark:text-dark-400 mb-4">Still have questions?</p>
            <Link to="/contact" className="btn-primary">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* Signup Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/50 dark:bg-dark-950/80 backdrop-blur-sm" onClick={closeModal} />
          <div ref={modalRef} className="relative card p-8 max-w-md w-full animate-scale-in">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-dark-800 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5 text-slate-500 dark:text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">You're all set!</h3>
                <p className="text-slate-600 dark:text-dark-300 mb-6">Welcome to {selectedPlan}! Check your email for next steps.</p>
                <button onClick={closeModal} className="btn-primary">Got it</button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">
                  {selectedLaunchOffer?.modalTitle ?? `Start with ${selectedPlan}`}
                </h3>
                <p className="text-slate-500 dark:text-dark-400 mb-6">
                  {selectedLaunchOffer?.modalDescription
                    ?? (selectedPlan === 'Free Trial'
                      ? 'Start your 3-month free trial. No credit card required.'
                      : 'Get started with your subscription today.')}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="modal-name" className="block text-sm font-medium text-slate-700 dark:text-dark-300 mb-2">Full Name *</label>
                    <input type="text" id="modal-name" name="name" value={formData.name} onChange={handleChange} className={`input-field ${errors.name ? 'border-red-500' : ''}`} placeholder="John Doe" />
                    {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="modal-email" className="block text-sm font-medium text-slate-700 dark:text-dark-300 mb-2">Work Email *</label>
                    <input type="email" id="modal-email" name="email" value={formData.email} onChange={handleChange} className={`input-field ${errors.email ? 'border-red-500' : ''}`} placeholder="john@company.com" />
                    {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="modal-company" className="block text-sm font-medium text-slate-700 dark:text-dark-300 mb-2">Company Name</label>
                    <input type="text" id="modal-company" name="company" value={formData.company} onChange={handleChange} className="input-field" placeholder="Acme Inc." />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating account...
                      </>
                    ) : (
                      selectedLaunchOffer?.modalSubmitLabel
                      ?? (selectedPlan === 'Free Trial' ? 'Start Free Trial' : 'Get Started')
                    )}
                  </button>
                </form>

                <p className="text-slate-400 dark:text-dark-500 text-xs text-center mt-4">By signing up, you agree to our Terms of Service and Privacy Policy.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
